import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Play, Pause, Send, Sparkles, CheckCircle2, Download } from "lucide-react";
import { api, API } from "@/lib/api";

const DOC_LABELS = { upd: "УПД", contract: "Договор поставки", act: "Акт выполненных работ", invoice: "Счёт-фактура" };
const FILTER_OF = { upd: "upd", contract: "contracts", act: "acts", invoice: "upd" };

function pick(filter) {
  const keys = filter === "all" ? Object.keys(DOC_LABELS) : Object.keys(DOC_LABELS).filter((k) => FILTER_OF[k] === filter);
  return keys[Math.floor(Math.random() * keys.length)] || "upd";
}

function CountUp({ target, suffix = "", duration = 1400 }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const pct = Math.min(1, (now - start) / duration);
      setVal(Math.floor(pct * target));
      if (pct < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return <>{val.toLocaleString("ru-RU")}{suffix}</>;
}

/** Simple flat-vector person. mirrored=true flips to face left (for the receiver). */
function Character({ cx, mirrored, shirt, skin, hair, bob }) {
  const s = mirrored ? -1 : 1;
  return (
    <motion.g animate={{ y: [0, -5, 0] }} transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut", delay: bob }}>
      {/* desk */}
      <rect x={cx - 95} y={244} width={190} height={13} rx={6} fill="#E2E8F0" />
      <rect x={cx - 40 * s - (s < 0 ? 40 : 0)} y={206} width={55} height={38} rx={5} fill="#94A3B8" />
      <rect x={cx - 40 * s - (s < 0 ? 40 : 0) + 4} y={210} width={47} height={26} rx={3} fill="#1E293B" />
      <rect x={cx - 40 * s - (s < 0 ? 40 : 0) + 7} y={213} width={24} height={3} rx={1.5} fill="#4E9BEE" opacity={0.8} />
      {/* legs/chair hint */}
      <rect x={cx - 22} y={238} width={44} height={10} rx={4} fill="#CBD5E1" />
      {/* torso */}
      <path d={`M ${cx - 32} 236 Q ${cx - 36} 178 ${cx} 174 Q ${cx + 36} 178 ${cx + 32} 236 Z`} fill={shirt} />
      {/* arm extended toward center */}
      <motion.path
        d={mirrored
          ? `M ${cx + 24} 196 Q ${cx + 60} 192 ${cx + 78} 176`
          : `M ${cx - 24} 196 Q ${cx - 60} 192 ${cx - 78} 176`}
        stroke={shirt}
        strokeWidth={15}
        strokeLinecap="round"
        fill="none"
        animate={{ rotate: [0, mirrored ? 6 : -6, 0] }}
        transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut", delay: bob }}
        style={{ transformOrigin: `${cx}px 196px` }}
      />
      {/* hand */}
      <circle cx={mirrored ? cx + 80 : cx - 80} cy={174} r={9} fill={skin} />
      {/* neck + head */}
      <rect x={cx - 8} y={160} width={16} height={14} fill={skin} />
      <circle cx={cx} cy={148} r={22} fill={skin} />
      {/* hair */}
      <path d={`M ${cx - 22} 144 Q ${cx - 24} 118 ${cx} 116 Q ${cx + 24} 118 ${cx + 22} 144 Q ${cx + 20} 128 ${cx} 128 Q ${cx - 20} 128 ${cx - 22} 144 Z`} fill={hair} />
      {/* smile */}
      <path d={`M ${cx - 7} 153 Q ${cx} 159 ${cx + 7} 153`} stroke="#8B5E3C" strokeWidth={2} fill="none" strokeLinecap="round" />
      {/* eyes */}
      <circle cx={cx - 7} cy={145} r={1.8} fill="#2D2420" />
      <circle cx={cx + 7} cy={145} r={1.8} fill="#2D2420" />
    </motion.g>
  );
}

const FILTERS = [
  { id: "all", label: "Все", testid: "landing-hero-filter-all" },
  { id: "upd", label: "УПД", testid: "landing-hero-filter-upd" },
  { id: "contracts", label: "Договоры", testid: "landing-hero-filter-contracts" },
  { id: "acts", label: "Акты", testid: "landing-hero-filter-acts" },
];

const FLOATING_BADGES = [
  { label: "ГОСТ ЭП", style: "left-[6%] top-[12%]", delay: 0 },
  { label: "CryptoDoc Direct", style: "right-[4%] top-[8%]", delay: 0.6 },
  { label: "100% Легитимно", style: "left-[4%] bottom-[10%]", delay: 1.1 },
];

export function LandingHero() {
  const [docs, setDocs] = useState([]);
  const [playing, setPlaying] = useState(true);
  const [filter, setFilter] = useState("all");
  const [stamp, setStamp] = useState(false);
  const [latestRelease, setLatestRelease] = useState(null);
  const nextId = useRef(0);
  const spawnedOnce = useRef(false);

  useEffect(() => {
    api
      .get("/releases/latest")
      .then(({ data }) => setLatestRelease(data))
      .catch(() => setLatestRelease(null));
  }, []);

  const spawnDoc = () => {
    const id = nextId.current++;
    setDocs((d) => [...d.slice(-3), { id, docKey: pick(filter) }]);
  };

  const removeDoc = (id) => {
    setDocs((d) => d.filter((x) => x.id !== id));
    setStamp(true);
    setTimeout(() => setStamp(false), 900);
  };

  useEffect(() => {
    if (!playing) return;
    if (!spawnedOnce.current) {
      spawnedOnce.current = true;
      spawnDoc();
    }
    const interval = setInterval(spawnDoc, 2600);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, filter]);

  return (
    <section id="top" className="relative overflow-hidden pt-28 pb-20 md:pt-36 md:pb-28 flex flex-col items-center text-center">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(#e2e8f0_1px,transparent_1px)] [background-size:22px_22px]" />
      <div className="absolute -z-10 top-10 left-1/4 w-72 h-72 rounded-full bg-[#4E9BEE]/10 blur-3xl" />
      <div className="absolute -z-10 top-24 right-1/4 w-72 h-72 rounded-full bg-[#10B981]/10 blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#4E9BEE]/30 bg-[#4E9BEE]/10 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#2563EB] mb-6">
          <Sparkles className="w-3 h-3" /> Юридически значимый ЭДО
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.1]" data-testid="landing-hero-title">
          Документы летают между<br className="hidden sm:block" /> коллегами и партнёрами за секунды
        </h1>
        <p className="mt-5 max-w-2xl mx-auto text-base sm:text-lg text-slate-600 leading-relaxed">
          КриптоДок — обмен, подписание и хранение юридически значимых документов между организациями.
          Отправили, согласовали, подписали ЭЦП — прямо в личном кабинете.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href={latestRelease ? `${API}/releases/latest/download` : "/register"}
            data-testid="landing-hero-cta-start"
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl text-sm font-semibold text-white bg-green-600 hover:bg-green-700 hover:scale-[1.02] active:scale-[0.98] transition-transform shadow-lg shadow-green-200"
          >
            <Download className="w-4 h-4" /> Установить в один клик
          </a>
        </div>
        <a href="#how-it-works" data-testid="landing-hero-cta-demo" className="inline-block mt-4 text-xs text-slate-400 hover:text-slate-600 transition-colors">
          или посмотреть как это работает ↓
        </a>

        <div
          className="relative mt-12 w-full max-w-4xl mx-auto rounded-3xl border border-slate-200 bg-white shadow-[0_12px_40px_-8px_rgba(15,23,42,0.12)] overflow-hidden"
          data-testid="landing-hero-flying-docs-container"
        >
          {FLOATING_BADGES.map((b) => (
            <motion.div
              key={b.label}
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut", delay: b.delay }}
              className={`hidden sm:flex absolute ${b.style} items-center gap-1.5 bg-white border border-slate-200 rounded-full px-3 py-1.5 shadow-sm text-[11px] font-semibold text-slate-600 z-20`}
            >
              <CheckCircle2 className="w-3 h-3 text-[#10B981]" /> {b.label}
            </motion.div>
          ))}

          <svg viewBox="0 0 600 300" className="w-full h-[260px] sm:h-[300px]">
            <Character cx={160} mirrored={false} shirt="#4E9BEE" skin="#F3C19C" hair="#2D2420" bob={0} />
            <Character cx={440} mirrored shirt="#10B981" skin="#E8A978" hair="#1F2937" bob={0.4} />
            <AnimatePresence>
              {stamp && (
                <motion.g initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: 1, scale: 1.1 }} exit={{ opacity: 0, scale: 1.4 }} transition={{ duration: 0.5 }}>
                  <circle cx={500} cy={150} r={22} fill="#4E9BEE" opacity={0.15} />
                  <circle cx={500} cy={150} r={14} fill="none" stroke="#4E9BEE" strokeWidth={3} />
                  <path d="M 493 150 L 498 156 L 508 143" stroke="#4E9BEE" strokeWidth={3} fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </motion.g>
              )}
            </AnimatePresence>
          </svg>

          <AnimatePresence>
            {docs.map((d) => (
              <motion.div
                key={d.id}
                className="absolute flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[10px] sm:text-[11px] font-semibold whitespace-nowrap z-10 bg-white"
                style={{ translateX: "-50%", translateY: "-50%" }}
                initial={{ left: "27%", top: "58%", opacity: 0, scale: 0.5 }}
                animate={{
                  left: ["27%", "50%", "73%"],
                  top: ["58%", "38%", "58%"],
                  opacity: [0, 1, 1, 0],
                  scale: [0.5, 1, 1, 0.6],
                  borderColor: ["rgba(100,116,139,0.4)", "rgba(16,185,129,0.6)", "rgba(78,155,238,0.7)"],
                  color: ["#64748B", "#059669", "#2563EB"],
                }}
                transition={{ duration: 2.8, ease: "easeInOut", times: [0, 0.15, 0.85, 1] }}
                onAnimationComplete={() => removeDoc(d.id)}
                data-testid={`flying-doc-${d.id}`}
              >
                <FileText className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
                {DOC_LABELS[d.docKey]}
              </motion.div>
            ))}
          </AnimatePresence>

          <div className="flex items-center justify-center gap-6 pb-5 -mt-1 text-xs font-medium text-slate-400">
            <span>Отправитель</span>
            <span>Получатель и подписант</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={f.testid}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                filter === f.id ? "border-[#4E9BEE]/50 bg-[#4E9BEE]/10 text-[#2563EB]" : "border-slate-200 text-slate-500 hover:border-slate-300"
              }`}
            >
              {f.label}
            </button>
          ))}
          <span className="w-px h-4 bg-slate-200 mx-1 hidden sm:inline-block" />
          <button
            onClick={() => setPlaying((p) => !p)}
            data-testid="landing-hero-play-pause-btn"
            className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-800 px-3 py-1.5 rounded-full border border-slate-200 transition-colors"
          >
            {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            {playing ? "Пауза" : "Продолжить"}
          </button>
          <button
            onClick={spawnDoc}
            data-testid="landing-hero-send-doc-btn"
            className="flex items-center gap-1.5 text-xs font-medium text-slate-700 hover:text-slate-900 px-3 py-1.5 rounded-full border border-slate-200 hover:border-[#4E9BEE]/50 transition-colors"
          >
            <Send className="w-3.5 h-3.5" /> Отправить документ
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3 sm:gap-6 max-w-2xl mx-auto mt-10">
          <div className="text-center" data-testid="landing-hero-metric-counter-docs">
            <div className="text-xl sm:text-3xl font-extrabold text-[#2563EB]"><CountUp target={142890} suffix="+" /></div>
            <div className="text-[10px] sm:text-xs text-slate-500 mt-1">Документов сегодня</div>
          </div>
          <div className="text-center" data-testid="landing-hero-metric-counter-speed">
            <div className="text-xl sm:text-3xl font-extrabold text-[#10B981]">0.8 сек</div>
            <div className="text-[10px] sm:text-xs text-slate-500 mt-1">Среднее время доставки</div>
          </div>
          <div className="text-center" data-testid="landing-hero-metric-counter-companies">
            <div className="text-xl sm:text-3xl font-extrabold text-slate-900"><CountUp target={45000} suffix="+" /></div>
            <div className="text-[10px] sm:text-xs text-slate-500 mt-1">Подключённых компаний</div>
          </div>
        </div>
      </div>
    </section>
  );
}
